import { motion } from "framer-motion";

function MiniAtlasAnimation() {
  const zones = [
    // Teal — left column
    { x: 8, y: 8, w: 96, h: 75, rx: 6, fill: "hsl(186 100% 18.4% / 0.14)", stroke: "hsl(186 100% 18.4% / 0.35)", postcode: "611 30", delay: 0.3 },
    { x: 8, y: 87, w: 96, h: 75, rx: 6, fill: "hsl(186 100% 18.4% / 0.10)", stroke: "hsl(186 100% 18.4% / 0.28)", postcode: "611 35", delay: 0.4 },
    // Coral — middle column
    { x: 108, y: 8, w: 100, h: 75, rx: 6, fill: "hsl(0 100% 70% / 0.10)", stroke: "hsl(0 100% 70% / 0.28)", postcode: "611 40", delay: 0.5 },
    { x: 108, y: 87, w: 100, h: 75, rx: 6, fill: "hsl(0 100% 70% / 0.07)", stroke: "hsl(0 100% 70% / 0.22)", postcode: "611 45", delay: 0.55 },
    // Turkos — right column
    { x: 212, y: 8, w: 100, h: 75, rx: 6, fill: "hsl(181 100% 33% / 0.10)", stroke: "hsl(181 100% 33% / 0.3)", postcode: "611 50", delay: 0.6 },
    { x: 212, y: 87, w: 100, h: 75, rx: 6, fill: "hsl(181 100% 33% / 0.07)", stroke: "hsl(181 100% 33% / 0.22)", postcode: "611 55", delay: 0.65 },
  ];

  const pins = [
    { cx: 56, cy: 55, delay: 0.9 },    // zone 1
    { cx: 158, cy: 55, delay: 1.0 },    // zone 3
    { cx: 262, cy: 134, delay: 1.1 },   // zone 6
  ];

  return (
    <svg viewBox="0 0 320 180" fill="none" className="w-full h-full">
      {/* Subtle road lines */}
      <motion.path d="M8 83 Q160 80, 312 83" stroke="hsl(186 100% 18.4% / 0.06)" strokeWidth={0.5} strokeDasharray="3 5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} />
      <motion.path d="M104 8 Q106 90, 108 162" stroke="hsl(186 100% 18.4% / 0.06)" strokeWidth={0.5} strokeDasharray="3 5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} />

      {/* Zone rectangles */}
      {zones.map((zone, i) => (
        <motion.g key={`zone-${i}`}>
          <motion.rect
            x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx={zone.rx}
            fill={zone.fill} stroke={zone.stroke} strokeWidth={1}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: zone.delay }}
          />
          <motion.text
            x={zone.x + zone.w / 2} y={zone.y + zone.h * 0.4}
            textAnchor="middle" fontSize="8"
            fill={zone.stroke}
            initial={{ opacity: 0 }} animate={{ opacity: 0.6 }}
            transition={{ delay: zone.delay + 0.2 }}
          >
            {zone.postcode}
          </motion.text>
        </motion.g>
      ))}

      {/* Map pins */}
      {pins.map((pin, i) => (
        <motion.g key={`pin-${i}`}>
          <motion.circle
            cx={pin.cx} cy={pin.cy} r={10}
            fill="hsl(0 100% 70% / 0.12)"
            initial={{ scale: 0 }}
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ delay: pin.delay + 0.1, duration: 2, repeat: Infinity }}
            style={{ transformOrigin: `${pin.cx}px ${pin.cy}px` }}
          />
          <motion.circle
            cx={pin.cx} cy={pin.cy} r={4}
            fill="hsl(0 100% 70%)"
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            transition={{ delay: pin.delay, type: "spring", stiffness: 300 }}
            style={{ transformOrigin: `${pin.cx}px ${pin.cy}px` }}
          />
        </motion.g>
      ))}
    </svg>
  );
}

function MiniValidAnimation() {
  return (
    <svg viewBox="0 0 320 180" fill="none" className="w-full h-full">
      {/* Address input field */}
      <motion.g
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <rect x={40} y={35} width={240} height={36} rx={6} fill="hsl(0 0% 100%)" stroke="hsl(186 100% 18.4% / 0.2)" strokeWidth={1} />
        <text x={54} y={58} fontSize="11" fontWeight="500" fill="hsl(190 15% 15%)">
          Kungsgatan 44, Stockholm
        </text>
      </motion.g>

      {/* Checkmark */}
      <motion.g
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.8, type: "spring", stiffness: 300 }}
      >
        <circle cx={262} cy={53} r={11} fill="hsl(140 60% 45%)" />
        <path d="M 255 53 L 260 58 L 269 49" stroke="hsl(0 0% 100%)" strokeWidth={2} strokeLinecap="round" fill="none" />
      </motion.g>

      {/* Enrichment attributes */}
      {[
        { label: "Län: Stockholm", y: 85 },
        { label: "Kommun: Stockholm", y: 108 },
        { label: "LKF-kod: 018001", y: 131 },
      ].map((attr, i) => (
        <motion.g
          key={`attr-${i}`}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.0 + i * 0.15 }}
        >
          <rect x={56} y={attr.y - 9} width={8} height={8} rx={2} fill="hsl(186 100% 18.4% / 0.15)" stroke="hsl(186 100% 18.4% / 0.3)" strokeWidth={0.5} />
          <text x={72} y={attr.y} fontSize="10" fill="hsl(190 8% 45%)">
            {attr.label}
          </text>
        </motion.g>
      ))}
    </svg>
  );
}

function MiniAdresstwattAnimation() {
  const addresses = [
    { text: "Storgtan 5", corrected: "Storgatan 5", status: "fixed" },
    { text: "Vägn 12B", corrected: "Vägen 12B", status: "fixed" },
    { text: "Björkallén 3", corrected: "Björkallén 3", status: "ok" },
  ];

  return (
    <svg viewBox="0 0 320 180" fill="none" className="w-full h-full">
      {addresses.map((addr, i) => {
        const y = 30 + i * 50;
        return (
          <motion.g
            key={`addr-${i}`}
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + i * 0.2 }}
          >
            {/* Row background */}
            <rect x={30} y={y} width={260} height={38} rx={6} fill="hsl(0 0% 100%)" stroke="hsl(186 100% 18.4% / 0.1)" strokeWidth={1} />

            {/* Original (strikethrough for fixed) */}
            {addr.status === "fixed" ? (
              <>
                <text x={44} y={y + 16} fontSize="10" fill="hsl(0 100% 50% / 0.5)" textDecoration="line-through">
                  {addr.text}
                </text>
                <motion.text
                  x={44}
                  y={y + 28}
                  fontSize="10"
                  fontWeight="600"
                  fill="hsl(140 60% 35%)"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 + i * 0.2 }}
                >
                  {addr.corrected}
                </motion.text>
              </>
            ) : (
              <text x={44} y={y + 23} fontSize="10" fontWeight="500" fill="hsl(190 15% 15%)">
                {addr.corrected}
              </text>
            )}

            {/* Status indicator */}
            <motion.g
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.8 + i * 0.15, type: "spring", stiffness: 300 }}
            >
              {addr.status === "fixed" ? (
                <>
                  <circle cx={270} cy={y + 19} r={9} fill="hsl(43 90% 61%)" />
                  <text x={270} y={y + 23} textAnchor="middle" fontSize="10" fontWeight="700" fill="hsl(0 0% 100%)">
                    ✓
                  </text>
                </>
              ) : (
                <>
                  <circle cx={270} cy={y + 19} r={9} fill="hsl(140 60% 45%)" />
                  <path d={`M 265 ${y + 19} L 268 ${y + 22} L 275 ${y + 15}`} stroke="hsl(0 0% 100%)" strokeWidth={1.5} strokeLinecap="round" fill="none" />
                </>
              )}
            </motion.g>
          </motion.g>
        );
      })}
    </svg>
  );
}

interface MiniProductAnimationProps {
  productKey: "atlas" | "valid" | "adresstwatt";
}

export function MiniProductAnimation({ productKey }: MiniProductAnimationProps) {
  switch (productKey) {
    case "atlas":
      return <MiniAtlasAnimation />;
    case "valid":
      return <MiniValidAnimation />;
    case "adresstwatt":
      return <MiniAdresstwattAnimation />;
  }
}
